import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, DatePicker, Form, Input, Row, Select, Space, Table, Tag, Tabs, Typography, message } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { transportApi } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import { getEnteredByUser } from "../../utils/currentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { LookupSelect } from "../../shared/components/LookupSelect";
import { StaticModal } from "../../shared/components/StaticModal";
import { BranchCreateModal } from "../../shared/components/BranchCreateModal";
import { PartyCreateModal } from "../../shared/components/PartyCreateModal";
import type { LookupItem as LookupItemType } from "../../shared/components/LookupModal";

type LookupItem = { id: number; label: string; subtitle?: string };
type SelectOpts = Array<{ value: number; label: string }>;

const toOpts = (items: LookupItem[]): SelectOpts =>
  items.map((x) => ({ value: x.id, label: x.subtitle ? `${x.label} - ${x.subtitle}` : x.label }));

export const OperationsPage = () => {
  const ctx = useMemo(() => getDefaultContext(), []);
  const navigate = useNavigate();
  const [globalSearch, setGlobalSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("manifests");

  const [vehicles, setVehicles] = useState<SelectOpts>([]);
  const [trips, setTrips] = useState<SelectOpts>([]);

  const [manifestRows, setManifestRows] = useState<any[]>([]);
  const [deliveryRows, setDeliveryRows] = useState<any[]>([]);

  const [manifestTransportMode, setManifestTransportMode] = useState<string>("Road");

  const [manifestOpen, setManifestOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  const [manifestForm] = Form.useForm();
  const [deliveryForm] = Form.useForm();
  const [trackingForm] = Form.useForm();

  const branchFetcher = useCallback((q: string, page: number, pageSize: number) => transportApi.searchBranchLookups(q, page, pageSize), []);
  const partyFetcher = useCallback((q: string, page: number, pageSize: number) => transportApi.searchPartyLookups(q, page, pageSize), []);

  // Inline create modals
  const [branchCreateOpen, setBranchCreateOpen] = useState(false);
  const [branchPrefill, setBranchPrefill]       = useState("");
  type ManifestBranchTarget = "fromBranchId" | "toBranchId";
  const [branchTarget, setBranchTarget]         = useState<ManifestBranchTarget>("fromBranchId");
  const [branchSeeds, setBranchSeeds]           = useState<LookupItemType[]>([]);

  const [partyCreateOpen, setPartyCreateOpen]   = useState(false);
  const [partyPrefill, setPartyPrefill]         = useState("");
  const [partySeeds, setPartySeeds]             = useState<LookupItemType[]>([]);
  const consignmentFetcher = useCallback((q: string, page: number, pageSize: number) => transportApi.searchConsignmentLookups(q, page, pageSize), []);

  const loadLookups = async () => {
    const [vehicleResult, tripResult] = await Promise.all([
      transportApi.searchVehicleLookups("", 100),
      transportApi.searchTripLookups("", 100, true),
    ]);
    setVehicles(toOpts(vehicleResult.items ?? []));
    setTrips(toOpts(tripResult.items ?? []));
  };

  const loadAllData = async (q = globalSearch) => {
    setLoading(true);
    try {
      const [manifests, deliveries] = await Promise.all([
        transportApi.listManifests({ page: 1, pageSize: 200, q: q || undefined }),
        transportApi.listDeliveries({ page: 1, pageSize: 200, q: q || undefined }),
      ]);
      setManifestRows(manifests.data);
      setDeliveryRows(deliveries.data);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load operations data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void Promise.all([loadLookups(), loadAllData("")]);
  }, []);

  const vehicleSelect = (
    <Select
      showSearch
      options={vehicles}
      placeholder="Select vehicle"
      filterOption={(input, opt) => (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())}
    />
  );

  const tripSelect = (optional = true) => (
    <Select
      showSearch
      options={trips}
      allowClear={optional}
      placeholder={optional ? "Select trip (optional)" : "Select trip"}
      filterOption={(input, opt) => (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())}
    />
  );

  const submitWithRefresh = async (key: string, work: () => Promise<void>, successMessage: string, close: () => void, reset?: () => void) => {
    setSaving(key);
    try {
      await work();
      message.success(successMessage);
      close();
      reset?.();
      await loadAllData();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save record"));
    } finally {
      setSaving(null);
    }
  };

  const fetchTimeline = async (values: any) => {
    try {
      const data = await transportApi.getTrackingTimeline(ctx.organizationId, ctx.financialYearId, values.consignmentId);
      setTimelineData(data ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to fetch tracking timeline"));
    }
  };

  const renderSection = (title: string, addText: string, onAdd: () => void, columns: any[], data: any[]) => (
    <Card
      title={title}
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>{addText}</Button>}
    >
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={data}
        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
        columns={[
          { title: "#", width: 70, render: (_: any, __: any, index: number) => index + 1 },
          ...columns
        ]}
      />
    </Card>
  );

  return (
    <Card className="panel-card">
      <Typography.Title level={4} style={{ margin: "0 0 16px" }}>Operations Utilities</Typography.Title>

      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Global search across manifests, deliveries, fuel, maintenance and expenses..."
          allowClear
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          onSearch={(value) => { setGlobalSearch(value); void loadAllData(value); }}
          style={{ width: 520 }}
        />
        <Typography.Text type="secondary">
          Global search reloads all operational tables from the server
        </Typography.Text>
      </Space>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyInactiveTabPane
        items={[
          {
            key: "manifests",
            label: "Manifests",
            children: renderSection("Manifest Register", "Add Manifest", () => setManifestOpen(true), [
              { title: "Manifest No", dataIndex: "manifestNo" },
              { title: "Vehicle", dataIndex: "vehicleId", width: 90 },
              { title: "Trip", dataIndex: "tripId", width: 90, render: (v: number | null) => v ?? "-" },
              { title: "Mode", dataIndex: "transportMode", width: 80, render: (v: string) => {
                const color: Record<string, string> = { Road: "blue", Rail: "orange", Air: "purple", Ship: "cyan", Multimodal: "geekblue" };
                return <Tag color={color[v] ?? "default"}>{v ?? "Road"}</Tag>;
              }},
              { title: "RR No", dataIndex: "railwayReceiptNo", width: 120, render: (v: string | null) => v ?? "-" },
              { title: "Dispatch Date", dataIndex: "dispatchDate", render: (v: string) => dayjs(v).format("DD MMM YYYY HH:mm") },
            ], manifestRows)
          },
          {
            key: "deliveries",
            label: "Deliveries",
            children: renderSection("Delivery Register", "Add Delivery", () => setDeliveryOpen(true), [
              { title: "Consignment", dataIndex: "consignmentId", width: 110 },
              { title: "Receiver", dataIndex: "receiverName" },
              { title: "Mobile", dataIndex: "receiverMobile", width: 130 },
              { title: "Delivered On", dataIndex: "deliveredOn", render: (v: string) => dayjs(v).format("DD MMM YYYY HH:mm") },
            ], deliveryRows)
          },
          {
            key: "tracking",
            label: "Tracking",
            children: (
              <Card title="Tracking Timeline">
                <Form form={trackingForm} layout="inline" onFinish={fetchTimeline} style={{ marginBottom: 16 }}>
                  <Form.Item name="consignmentId" label="Consignment" rules={[{ required: true }]}>
                    <LookupSelect fetcher={consignmentFetcher} modalTitle="Select Consignment" placeholder="Search Bilty / LR no..." />
                  </Form.Item>
                  <Button type="primary" htmlType="submit">Fetch Timeline</Button>
                </Form>
                <Table
                  rowKey={(_, index) => String(index)}
                  size="small"
                  dataSource={timelineData}
                  pagination={{ pageSize: 8, showSizeChanger: false }}
                  columns={[
                    { title: "#", width: 70, render: (_: any, __: any, index: number) => index + 1 },
                    { title: "Event", render: (_: any, row: any) => row.eventType ?? row.type ?? "Event" },
                    { title: "Details", render: (_: any, row: any) => row.message ?? row.description ?? "" },
                  ]}
                />
              </Card>
            )
          },
          {
            key: "trip-logs",
            label: "Fuel / Expenses / Maintenance",
            children: (
              <Card>
                <Alert
                  type="info"
                  showIcon
                  message="Trip-level logging has moved"
                  description={
                    <span>
                      Fuel logs, trip expenses, and maintenance records are now managed from the{" "}
                      <Typography.Link onClick={() => navigate("/trips")}>Trip Planning</Typography.Link>{" "}
                      page. Open any trip and use the Details button to log fuel, expenses, or maintenance against that specific trip.
                    </span>
                  }
                  action={
                    <Button size="small" onClick={() => navigate("/trips")}>Go to Trips</Button>
                  }
                  style={{ maxWidth: 600 }}
                />
              </Card>
            )
          }
        ]}
      />

      <StaticModal title="Add Manifest" open={manifestOpen} onCancel={() => { setManifestOpen(false); setManifestTransportMode("Road"); }} onOk={() => manifestForm.submit()} confirmLoading={saving === "manifest"} width={760} destroyOnHidden>
        <Form
          form={manifestForm}
          layout="vertical"
          initialValues={{ dispatchDate: dayjs(), transportMode: "Road" }}
          onValuesChange={(changed) => { if (changed.transportMode) setManifestTransportMode(changed.transportMode); }}
          onFinish={(values) => void submitWithRefresh("manifest", async () => {
            await transportApi.createManifest({
              ...ctx, enteredBy: getEnteredByUser(),
              vehicleId: values.vehicleId,
              tripId: values.tripId ?? null,
              dispatchDate: values.dispatchDate.toISOString(),
              fromBranchId: values.fromBranchId,
              toBranchId: values.toBranchId,
              transportMode: values.transportMode ?? "Road",
              railwayReceiptNo: values.transportMode === "Rail" ? (values.railwayReceiptNo ?? null) : null,
              trainNo: values.transportMode === "Rail" ? (values.trainNo ?? null) : null,
              wagonNo: values.transportMode === "Rail" ? (values.wagonNo ?? null) : null,
              remarks: values.remarks
            });
          }, "Manifest created", () => { setManifestOpen(false); setManifestTransportMode("Road"); }, () => manifestForm.resetFields())}
        >
          <Row gutter={12}>
            <Col span={12}><Form.Item name="vehicleId" label="Lorry / Vehicle No" rules={[{ required: true }]}>{vehicleSelect}</Form.Item></Col>
            <Col span={12}><Form.Item name="tripId" label="Trip">{tripSelect(true)}</Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="transportMode" label="Transport Mode" rules={[{ required: true }]}>
                <Select options={[
                  { value: "Road", label: "1 — Road" },
                  { value: "Rail", label: "2 — Rail" },
                  { value: "Air",  label: "3 — Air" },
                  { value: "Ship", label: "4 — Ship" },
                  { value: "Multimodal", label: "5 — Multimodal" },
                ]} />
              </Form.Item>
            </Col>
            <Col span={8}><Form.Item name="dispatchDate" label="Dispatch Date" rules={[{ required: true }]}><DatePicker showTime style={{ width: "100%" }} /></Form.Item></Col>
            <Col span={8}>
              <Form.Item
                name="railwayReceiptNo"
                label="RR No (Railway Receipt)"
                rules={[{ required: manifestTransportMode === "Rail", message: "RR No is required for Rail mode" }]}
                style={{ display: manifestTransportMode === "Rail" ? undefined : "none" }}
              >
                <Input placeholder="Enter Railway Receipt No." />
              </Form.Item>
            </Col>
          </Row>
          {manifestTransportMode === "Rail" && (
            <Row gutter={12}>
              <Col span={12}><Form.Item name="trainNo" label="Train No"><Input placeholder="e.g. 12345" /></Form.Item></Col>
              <Col span={12}><Form.Item name="wagonNo" label="Wagon / Coach No"><Input placeholder="e.g. WR-8745" /></Form.Item></Col>
            </Row>
          )}
          <Row gutter={12}>
            <Col span={12}><Form.Item name="fromBranchId" label="From Branch / Station" rules={[{ required: true }]}><LookupSelect fetcher={branchFetcher} placeholder="Search branch / station..." addNewLabel="Branch / Station" seededOptions={branchSeeds} onAddNew={(q) => { setBranchPrefill(q); setBranchTarget("fromBranchId"); setBranchCreateOpen(true); }} /></Form.Item></Col>
            <Col span={12}><Form.Item
              name="toBranchId"
              label="To Branch / Station"
              dependencies={["fromBranchId"]}
              rules={[
                { required: true, message: "Select the destination branch" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return value && value === getFieldValue("fromBranchId")
                      ? Promise.reject(new Error("From and To Branch / Station must be different."))
                      : Promise.resolve();
                  },
                }),
              ]}
            ><LookupSelect fetcher={branchFetcher} placeholder="Search branch / station..." addNewLabel="Branch / Station" seededOptions={branchSeeds} onAddNew={(q) => { setBranchPrefill(q); setBranchTarget("toBranchId"); setBranchCreateOpen(true); }} /></Form.Item></Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </StaticModal>

      <StaticModal title="Add Delivery" open={deliveryOpen} onCancel={() => setDeliveryOpen(false)} onOk={() => deliveryForm.submit()} confirmLoading={saving === "delivery"} width={720} destroyOnHidden>
        <Form form={deliveryForm} layout="vertical" initialValues={{ deliveredOn: dayjs() }} onFinish={(values) => void submitWithRefresh("delivery", async () => {
          await transportApi.createDelivery({
            ...ctx, enteredBy: getEnteredByUser(),
            partyId: values.partyId,
            consignmentId: values.consignmentId,
            receiverName: values.receiverName,
            receiverMobile: values.receiverMobile,
            deliveredOn: values.deliveredOn.toISOString(),
            podUploaded: false,
            deliveryRemarks: values.deliveryRemarks
          });
        }, "Delivery posted", () => setDeliveryOpen(false), () => deliveryForm.resetFields())}>
          <Form.Item name="partyId" label="Party" rules={[{ required: true }]}><LookupSelect fetcher={partyFetcher} placeholder="Search party..." addNewLabel="Party" seededOptions={partySeeds} onAddNew={(q) => { setPartyPrefill(q); setPartyCreateOpen(true); }} /></Form.Item>
          <Form.Item name="consignmentId" label="Consignment" rules={[{ required: true }]}><LookupSelect fetcher={consignmentFetcher} modalTitle="Select Consignment" placeholder="Search Bilty / LR no..." /></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="receiverName" label="Receiver Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="receiverMobile" label="Receiver Mobile" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="deliveredOn" label="Delivered On" rules={[{ required: true }]}><DatePicker showTime style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="deliveryRemarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </StaticModal>

      <BranchCreateModal
        open={branchCreateOpen}
        prefillName={branchPrefill}
        onClose={() => setBranchCreateOpen(false)}
        onCreated={(item) => {
          setBranchSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          manifestForm.setFieldValue(branchTarget, item.id);
          setBranchCreateOpen(false);
        }}
      />
      <PartyCreateModal
        open={partyCreateOpen}
        prefillName={partyPrefill}
        onClose={() => setPartyCreateOpen(false)}
        onCreated={(item) => {
          setPartySeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          deliveryForm.setFieldValue("partyId", item.id);
          setPartyCreateOpen(false);
        }}
      />
    </Card>
  );
};
